import { useContext, useEffect, useState } from "react";
import { api, loadCurrentUser, MainContext } from "../AppContext";
import LoginPhoneInput from "./inputs/LoginPhoneInput";
import moment from "moment";
import { Formik, Form, Field } from "formik";
import toast from "react-hot-toast";
import OTPInput from "./inputs/OTPInput";
import { FaWhatsapp } from "react-icons/fa";

export default function Login() {
  const { setToken } = useContext(MainContext);
  const [phone, setPhone] = useState("");
  const [stage, setStage] = useState<"phone_number" | "otp">("phone_number");
  const [tryAgain, setTryAgain] = useState(0);
  const [remainingTime, setRemainingTime] = useState({
    minutes: 0,
    seconds: 0,
  });
  const [selectedCountry, setSelectedCountry] = useState("966");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const now = moment();
    const futureTime = now.add(tryAgain, "seconds");

    const calculate = () => {
      const remainingTime = moment.duration(futureTime.diff(moment()));
      const minutes = remainingTime.minutes();
      const seconds = remainingTime.seconds();
      setRemainingTime({ minutes, seconds });
    };

    const interval = setInterval(calculate, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [tryAgain]);

  useEffect(() => {
    // Focus first OTP input when stage changes to "otp"
    if (stage === "otp") {
      const firstInput = document.getElementById("otp-input-0");
      if (firstInput) {
        firstInput.focus();
      }
    }
  }, [stage]);

  const handleLogin = (values: { otp: string; sendViaWhatsapp: boolean }) => {
    setIsLoading(true);
    api
      .post(
        "/login",
        {
          phone_number: phone,
        },
        {
          headers: { otp: values.otp },
        }
      )
      .then((res) => {
        const token = res.data.token;
        return loadCurrentUser(token).then((user) => ({ token, user }));
      })
      .then(({ token, user }) => {
        if (!user.first_name) {
          toast.error("Please complete your profile");
        }
        if (user.roles?.length) {
          localStorage.setItem("token", token);
          setToken(token);
          toast.success("Login successful!");
        } else {
          toast.error("You are not authorized to access this platform");
        }
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.error || "An error occurred. Please try again.";
        toast.error(errorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  const handlePhoneSubmit = (
    phone_number: string,
    sendViaWhatsapp: boolean
  ) => {
    if (phone_number.length < 8) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setIsLoading(true);
    const fullPhoneNumber = `${selectedCountry}${phone_number.replace(
      /^0+/,
      ""
    )}`;
    setPhone(fullPhoneNumber);

    api
      .post("/request_otp", {
        phone_number: fullPhoneNumber,
        provider: sendViaWhatsapp ? "whatsapp" : "sms",
      })
      .then((res) => {
        setStage("otp");
        setTryAgain(res.data.try_again);
        toast.success("OTP sent successfully");
      })
      .catch((err) => {
        const errorMessage =
          err.response?.status === 429
            ? "OTP already sent. Please use the existing OTP."
            : "An error occurred. Please try again.";
        setStage("otp");
        setTryAgain(err.response?.data?.try_again || 0);
        toast.error(errorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-foreground flex flex-col items-center justify-center px-6 py-8">
      <div className="w-full max-w-sm">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 w-10 h-10 rounded-md bg-black dark:bg-white flex items-center justify-center text-white dark:text-black font-bold text-base">
            R
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Sign in to Fulfillment
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            Enter your phone number to continue
          </p>
        </div>

        <div className="rounded-lg p-6 border border-border bg-card text-card-foreground">
          <Formik
            initialValues={{ phone: "", otp: "", sendViaWhatsapp: false }}
            onSubmit={(values) => {
              if (stage === "phone_number") {
                handlePhoneSubmit(values.phone, values.sendViaWhatsapp);
              } else {
                handleLogin(values);
              }
            }}
          >
            {({ values, setFieldValue, handleSubmit }) => (
              <Form
                className="space-y-5"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              >
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                    Phone number
                  </label>
                  <LoginPhoneInput
                    disabled={stage !== "phone_number"}
                    value={values.phone}
                    onChange={(phone) => setFieldValue("phone", phone)}
                    setSelectedCountry={setSelectedCountry}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>

                {stage === "phone_number" && (
                  <label className="flex items-center gap-2.5 p-3 rounded-md bg-muted border border-border cursor-pointer hover:bg-accent transition-colors">
                    <Field
                      type="checkbox"
                      name="sendViaWhatsapp"
                      className="hidden"
                    />
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                        values.sendViaWhatsapp
                          ? "bg-black dark:bg-white border-black dark:border-white"
                          : "bg-card text-card-foreground border-gray-300 dark:border-[#3f3f46]"
                      }`}
                    >
                      {values.sendViaWhatsapp && (
                        <FaWhatsapp className="w-2.5 h-2.5 text-white dark:text-black" />
                      )}
                    </div>
                    <span className="text-sm text-foreground">
                      Send code via WhatsApp
                    </span>
                  </label>
                )}

                {stage === "otp" && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                        Verification code
                      </label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Sent to {phone}
                      </p>
                      <OTPInput
                        otp={values.otp}
                        setFieldValue={setFieldValue}
                      />
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => {
                          setStage("phone_number");
                          setFieldValue("otp", "");
                        }}
                        className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                      >
                        ← Change number
                      </button>

                      {remainingTime.minutes <= 0 &&
                        remainingTime.seconds <= 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              handlePhoneSubmit(
                                values.phone,
                                values.sendViaWhatsapp
                              )
                            }
                            className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                          >
                            Resend code
                          </button>
                        )}
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-md text-sm font-medium bg-primary text-primary-foreground border border-black dark:border-white hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading && (
                    <span
                      aria-hidden
                      className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                    />
                  )}
                  <span>
                    {isLoading
                      ? "Processing…"
                      : stage === "phone_number"
                        ? "Send code"
                        : "Sign in"}
                  </span>
                </button>
              </Form>
            )}
          </Formik>

          {stage === "otp" &&
            !(remainingTime.minutes <= 0 && remainingTime.seconds <= 0) && (
              <p className="mt-4 text-xs text-muted-foreground text-center">
                You can request a new code in{" "}
                <span className="text-foreground font-medium">
                  {remainingTime.minutes}m {remainingTime.seconds}s
                </span>
              </p>
            )}
        </div>

        <p className="text-center text-xs text-muted-foreground dark:text-muted-foreground mt-6">
          Authorized personnel only.
        </p>
      </div>
    </div>
  );
}
